import { useEffect, useState } from 'react';

import { ShiftsService } from '../../api/ShiftsAPI';
import { EditClockModal } from './EditClockModal';
import s from '../../styles/staff.module.css';

// One technician's clock activity for the period, as a side panel.
//
// A drawer rather than an expanding row: the row sits in a table that already
// scrolls, and opening eight days of detail inside it pushed everybody else
// off the screen. Here the table stays put and the detail sits beside it.
//
// Editing opens its own modal rather than swapping fields into the card: the
// correction asks for a reason as well as two times, and a form that pushes
// the rest of the week down the panel while it is filled in loses the days
// somebody was comparing it against.
//
// Editing and deleting both archive the original row rather than overwriting
// it. Nothing here destroys a timecard somebody may have been paid against.

const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const dur = (mins) => {
  const n = Math.max(0, Math.round(mins || 0));
  if (!n) return '—';
  const h = Math.floor(n / 60);
  return h ? `${h}h${n % 60 ? ` ${n % 60}m` : ''}` : `${n}m`;
};
const hrs = (mins) => (mins ? `${+(mins / 60).toFixed(1)}h` : '0h');
const tm = (v) => (v ? new Date(v).toLocaleTimeString('en-US',
  { hour: 'numeric', minute: '2-digit' }) : '—');
const utcDay = (key) => new Date(`${key}T00:00:00Z`);
const dayLabel = (key) => utcDay(key).toLocaleDateString('en-US',
  { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });

export function TimesheetDrawer({
  person, detail, range, weekDays, busy: pageBusy,
  onClose, onChanged, onApprove, onReopen,
}) {
  const [editing, setEditing] = useState(null);
  const [ownBusy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // The page is mid-write too while it signs the week off from here.
  const busy = ownBusy || pageBusy;

  useEffect(() => {
    // While the edit modal is up, Escape belongs to it — otherwise one press
    // would close the modal and the panel behind it together.
    const onKey = (e) => { if (e.key === 'Escape' && !editing) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, editing]);

  const days = detail?.days || [];
  const maxMins = Math.max(480, ...days.map(d => Math.max(d.workedMinutes, d.scheduledMinutes)));

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError(err?.error || err?.message || 'That did not go through.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={s.tsScrim} onClick={busy ? undefined : onClose} />
      <aside className={s.tsDrawer} role="dialog" aria-label={`Clock activity for ${person.name}`}>
        <div className={s.tsDrawerHead}>
          <div>
            <div className={s.shiftLabel}>
              Clock activity · {dayLabel(range.start)} – {dayLabel(range.end)}
            </div>
            <div className={s.dayTitle}>{person.name}</div>
            <div className={s.daySub}>{person.locationNames.join(' + ') || '—'}</div>
          </div>
          <button className={s.schedClose} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className={s.tsDrawerBody}>
          <div className={s.tsDrawerStats}>
            <div className={s.schedStat}>
              <div className={s.schedStatLabel}>Worked</div>
              <div className={s.schedStatValue}>{hrs(person.workedMinutes)}</div>
            </div>
            <div className={s.schedStat}>
              <div className={s.schedStatLabel}>Scheduled</div>
              <div className={`${s.schedStatValue} ${s.attnToneGray}`}>{hrs(person.scheduledMinutes)}</div>
            </div>
            <div className={s.schedStat}>
              <div className={s.schedStatLabel}>Exceptions</div>
              <div className={`${s.schedStatValue} ${person.exceptionCount ? s.attnToneDue : ''}`}>
                {person.exceptionCount}
              </div>
            </div>
          </div>

          {/* Signing the week off used to live in the table row. The row now
              opens this panel, so the decision is made where the hours it is
              about are actually visible. */}
          <div className={s.tsSignOff}>
            <span className={s.daySub}>
              {person.approved
                ? person.approved.drifted
                  ? 'Approved, but the hours have changed since.'
                  : 'This week is signed off.'
                : 'Not signed off yet.'}
            </span>
            {person.approved ? (
              <button className={s.dayAction} disabled={busy} onClick={onReopen}>Reopen</button>
            ) : (
              <button className={s.schedPost} disabled={busy} onClick={onApprove}>Approve week</button>
            )}
          </div>

          <div className={s.weekCard}>
            <div className={s.weekBars}>
              {weekDays.map((key, i) => {
                const d = days.find(x => x.day === key) || {};
                return (
                  <div key={key} className={s.weekBarCol}>
                    <div className={s.weekBarTrack}>
                      <div className={s.weekBarSched}
                           style={{ height: `${((d.scheduledMinutes || 0) / maxMins) * 100}%` }} />
                      <div className={s.weekBarWork}
                           style={{ height: `${((d.workedMinutes || 0) / maxMins) * 100}%` }} />
                    </div>
                    <span className={s.weekBarDow}>{DOW[i]}</span>
                  </div>
                );
              })}
            </div>
            <div className={s.weekLegend}>
              <span><i className={s.legendWork} />Worked</span>
              <span><i className={s.legendSched} />Scheduled</span>
            </div>
          </div>

          {error && <div className={s.attnError}>{error}</div>}

          {!days.length && (
            <div className={s.railEmpty}>Nothing planned or clocked in this period.</div>
          )}

          {days.map(d => {
            const plan = d.scheduled[0];
            return (
              <div key={d.day} className={s.tsDayCard}>
                <div className={s.tsDayCardHead}>
                  <span className={s.tsDayCardWhen}>{dayLabel(d.day)}</span>
                  {d.worked.some(w => w.open) && (
                    <span className={`${s.statusPill} ${s.attnToneGray}`}>on shift</span>
                  )}
                  {!d.worked.length && plan && (
                    <span className={`${s.statusPill} ${s.attnToneDue}`}>no punch</span>
                  )}
                  {/* These times were typed by an admin, not clocked. */}
                  {d.worked.some(w => w.edited) && (
                    <span className={`${s.statusPill} ${s.attnToneWarn}`}>edited</span>
                  )}
                  <span className={s.tsDayCardWhere}>{plan?.locationName || d.worked[0]?.locationName || ''}</span>
                </div>
                <div className={s.daySub}>
                  {plan ? `Scheduled ${tm(plan.startAt)} – ${tm(plan.endAt)}` : 'Not scheduled'}
                </div>

                {d.worked.map(w => (
                  <div key={w.idShift} className={s.tsPunch}>
                    <div className={s.tsPunchRow}>
                      <span className={s.tsPunchCell}>
                        <span className={s.shiftLabel}>Clock in</span>
                        <span className={s.tsPunchVal}>{tm(w.clockInAt)}</span>
                      </span>
                      <span className={s.tsPunchCell}>
                        <span className={s.shiftLabel}>Clock out</span>
                        <span className={s.tsPunchVal}>{w.open ? '—' : tm(w.clockOutAt)}</span>
                      </span>
                      <span className={s.tsPunchCell}>
                        <span className={s.shiftLabel}>Hours</span>
                        <span className={s.tsPunchVal}>{dur(w.minutes)}</span>
                      </span>
                    </div>
                    <div className={s.tsPunchActions}>
                      <button className={s.dayAction} disabled={busy}
                              onClick={() => setEditing({ day: d.day, plan, entry: w })}>
                        Edit
                      </button>
                      <button className={s.tsDelete} disabled={busy}
                              onClick={() => run(() => ShiftsService.remove(w.idShift))}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}

                {!d.worked.length && (
                  <div className={s.railEmpty} style={{ border: 'none', padding: '10px 0 0' }}>
                    Nothing clocked on this day.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>

      {editing && (
        <EditClockModal
          person={person} day={editing.day} plan={editing.plan}
          entry={editing.entry}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChanged(); }} />
      )}
    </>
  );
}
